import React, { useState, useEffect } from 'react';
import { useHistory, Link } from 'react-router-dom';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, 
    Paper, Button, CircularProgress, Chip, IconButton, TextField, InputAdornment
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import { fetchData, deleteData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import Pagination from '../../shared/Pagination';
import './Index.css';

const SubContractIndex = () => {
    const { token, companyId, isAuthenticated, logout, permissions } = useAuth();
    const history = useHistory();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    
    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    const hasCreateMasterPermission = permissions.includes('Create Master');
    const hasEditMasterPermission = permissions.includes('Edit Master');
    const hasDeleteMasterPermission = permissions.includes('Delete Master');

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
        loadSubContracts();
    }, [isAuthenticated, token]);

    const loadSubContracts = async () => {
        setLoading(true);
        try {
            const response = await fetchData('SubContractAPI', `/${companyId}`, token);
            if (response.responseCode === '1111') {
                setData(response.response_data || []);
            } else {
                setData([]);
            }
        } catch (err) {
            console.error('Failed to fetch subcontracts', err);
            setData([]);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this subcontract?')) {
            try {
                await deleteData('SubContractAPI', `${id}`, token);
                loadSubContracts();
            } catch (error) {
                console.error('Error deleting subcontract:', error);
                alert('Failed to delete subcontract');
            }
        }
    };

    const filteredData = data.filter((item) =>
        (item.Name && item.Name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.ContactNumber && item.ContactNumber.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.State && item.State.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    // Pagination logic
    const totalItems = filteredData.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedData = filteredData.slice(startIndex, endIndex);

    // Reset to first page when search changes
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm]);

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleItemsPerPageChange = (newItemsPerPage) => {
        setItemsPerPage(newItemsPerPage);
        setCurrentPage(1);
    };



    return (
        <div className="subcontract-index-wrapper">
            {/* Header */}
            <div className="subcontract-index-header">
                <div className="header-left">
                    <h1 className="index-title">Sub Contractors</h1>
                    <p className="index-subtitle">Manage all subcontractor details</p>
                </div>
                {hasCreateMasterPermission && (
                    <Button
                        variant="contained"
                        component={Link}
                        to="/SubContractCreate"
                        className="btn-create"
                        startIcon={<AddIcon />}
                    >
                        Create
                    </Button>
                )}
            </div>

            {/* Filters */}
            <div className="filters-section">
                <TextField
                    placeholder="Search by Name, Contact, or State..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="search-input"
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                />
            </div>

            {/* Table */}
            <div className="table-container">
                {loading ? (
                    <div className="loading-container">
                        <CircularProgress />
                        <p>Loading subcontracts...</p>
                    </div>
                ) : filteredData.length === 0 ? (
                    <div className="empty-state">
                        <div className="empty-icon">👷</div>
                        <h3>No SubContracts Found</h3>
                        <p>Create your first subcontract to get started</p>
                        {hasCreateMasterPermission && (
                            <Button
                                variant="contained"
                                component={Link}
                                to="/SubContractCreate"
                                className="btn-create"
                                startIcon={<AddIcon />}
                            >
                                Create 
                            </Button>
                        )}
                    </div>
                ) : (
                    <TableContainer component={Paper} className="modern-table">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell className="table-header">Sl No</TableCell>
                                    <TableCell className="table-header">Name</TableCell>
                                    <TableCell className="table-header">Contact Number</TableCell>
                                    <TableCell className="table-header">State</TableCell>
                                    <TableCell className="table-header" align="center">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {paginatedData.map((item, index) => (
                                    <TableRow key={item.Id} className="table-row">
                                        <TableCell className="table-cell">
                                            <span className="serial-number">{startIndex + index + 1}</span>
                                        </TableCell>
                                        <TableCell className="table-cell">
                                            <span className="subcontract-name">{item.Name}</span>
                                        </TableCell>
                                        <TableCell className="table-cell">
                                            <span className="contact-text">{item.ContactNumber}</span>
                                        </TableCell>
                                        <TableCell className="table-cell">
                                            <span className="state-text">{item.State || '-'}</span>
                                        </TableCell>
                                        <TableCell className="table-cell" align="center">
                                            <div className="action-buttons">
                                                {hasEditMasterPermission && (
                                                    <IconButton
                                                        size="small"
                                                        className="action-btn edit-btn"
                                                        component={Link}
                                                        to={`/SubContractEdit/${item.Id}`}
                                                        title="Edit"
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                )}
                                                {hasDeleteMasterPermission && (
                                                    <IconButton
                                                        size="small"
                                                        className="action-btn delete-btn"
                                                        onClick={() => handleDelete(item.Id)}
                                                        title="Delete"
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </div>

            {/* Pagination */}
            {!loading && filteredData.length > 0 && (
                <Pagination
                    currentPage={currentPage}
                    totalItems={totalItems}
                    itemsPerPage={itemsPerPage}
                    onPageChange={handlePageChange}
                    onItemsPerPageChange={handleItemsPerPageChange}
                />
            )}

            {/* Summary */}
            {!loading && filteredData.length > 0 && (
                <div className="summary-section">
                    <span className="summary-text">
                        Total: {data.length} subcontracts
                    </span>
                </div>
            )}
        </div>
    );
};

export default SubContractIndex;